import { useRef, useState } from 'react';
import { Surface } from '@cloudflare/kumo/components/surface';
import { Field } from '@cloudflare/kumo/components/field';
import { Input, Textarea } from '@cloudflare/kumo/components/input';
import { Select } from '@cloudflare/kumo/components/select';
import { Checkbox } from '@cloudflare/kumo/components/checkbox';
import { Button } from '@cloudflare/kumo/components/button';
import { Banner } from '@cloudflare/kumo/components/banner';
import { FloppyDisk, Trash, UploadSimple } from '@phosphor-icons/react';
import { api } from '@/lib/api';
import { exhibitorAdminFullUpdate2026Schema } from '@nt-tokyo/shared';

// 出展者が申込フォームで入力した内容を、管理者がまとめて編集するパネル。
// ステータス / ブース番号 / 料金override / 管理者メモ は ExhibitorEditPanel 側で扱う。
// email はログインアカウントに紐づくためここでは編集不可。

interface EditableExhibitor {
  id: string;
  status: string;
  realName: string;
  exhibitorName: string;
  phone: string;
  website: string | null;
  twitterId: string | null;
  type: string;
  exhibitTitle: string;
  exhibitDescription: string;
  images: string[];
  exhibitDays: string[];
  deliveryMethods: string[] | null;
  specialRequests: string | null;
}

interface Year2026Detail {
  exhibitorId: string;
  tableCount: number;
  chairCount: number;
  powerWatts: number;
  sponsorUnits: number;
  sponsorDisplayName: string | null;
}

interface Props {
  exhibitor: EditableExhibitor;
  yearDetail: Year2026Detail | null;
  onUpdate: () => void;
}

const TYPE_OPTIONS = [
  { value: 'general', label: '一般' },
  { value: 'student-university', label: '学生 (大学)' },
  { value: 'student-high-school', label: '学生 (高校以下)' },
  { value: 'corporate', label: '企業' },
];

const TABLE_COUNT_OPTIONS = [0, 1, 2];
const CHAIR_COUNT_OPTIONS = [0, 1, 2, 3, 4];
const POWER_WATTS_OPTIONS = [0, 100, 200, 300, 400, 500, 600, 700, 800, 900, 1000];

const MAX_IMAGES = 5;

export default function ExhibitorApplicationEditPanel({ exhibitor, yearDetail, onUpdate }: Props) {
  const [realName, setRealName] = useState(exhibitor.realName);
  const [exhibitorName, setExhibitorName] = useState(exhibitor.exhibitorName);
  const [phone, setPhone] = useState(exhibitor.phone);
  const [website, setWebsite] = useState(exhibitor.website ?? '');
  const [twitterId, setTwitterId] = useState(exhibitor.twitterId ?? '');
  const [type, setType] = useState(exhibitor.type);
  const [exhibitTitle, setExhibitTitle] = useState(exhibitor.exhibitTitle);
  const [exhibitDescription, setExhibitDescription] = useState(exhibitor.exhibitDescription);
  const [images, setImages] = useState<string[]>(exhibitor.images ?? []);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [exhibitDays, setExhibitDays] = useState<string[]>(exhibitor.exhibitDays ?? []);
  const [deliveryMethods, setDeliveryMethods] = useState<string[]>(exhibitor.deliveryMethods ?? []);
  const [specialRequests, setSpecialRequests] = useState(exhibitor.specialRequests ?? '');
  const [tableCount, setTableCount] = useState(yearDetail?.tableCount ?? 0);
  const [chairCount, setChairCount] = useState(yearDetail?.chairCount ?? 0);
  const [powerWatts, setPowerWatts] = useState(yearDetail?.powerWatts ?? 0);
  const [sponsorUnits, setSponsorUnits] = useState(yearDetail?.sponsorUnits ?? 0);
  const [sponsorDisplayName, setSponsorDisplayName] = useState(yearDetail?.sponsorDisplayName ?? '');

  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'ok' | 'err'; msg: string } | null>(null);

  const handleImageUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    if (images.length + files.length > MAX_IMAGES) {
      setFeedback({ type: 'err', msg: `画像は最大 ${MAX_IMAGES} 枚までです` });
      return;
    }
    setUploading(true);
    setFeedback(null);
    try {
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append('image', file);
        const res = await api.post<{ url: string }>('/api/images/upload', formData);
        setImages((prev) => [...prev, res.data.url]);
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        '画像のアップロードに失敗しました';
      setFeedback({ type: 'err', msg: message });
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setFeedback(null);

    const payload = {
      realName,
      exhibitorName,
      phone,
      website,
      twitterId,
      exhibitTitle,
      exhibitDescription,
      images,
      type,
      specialRequests,
      exhibitDays,
      deliveryMethods: deliveryMethods.length > 0 ? deliveryMethods : null,
      tableCount,
      chairCount,
      powerWatts,
      sponsorUnits,
      sponsorDisplayName: sponsorDisplayName || null,
    };

    const validation = exhibitorAdminFullUpdate2026Schema.safeParse(payload);
    if (!validation.success) {
      setFeedback({ type: 'err', msg: validation.error.issues[0]?.message ?? '入力内容に誤りがあります' });
      setBusy(false);
      return;
    }

    try {
      await api.put(`/api/admin/exhibitors/${exhibitor.id}`, payload);
      setFeedback({ type: 'ok', msg: '申込内容を更新しました' });
      onUpdate();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '申込内容の更新に失敗しました';
      setFeedback({ type: 'err', msg: message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Surface className="p-6">
      <h2 className="mb-1 text-base font-semibold text-kumo-default">申込内容の編集</h2>
      <p className="mb-4 text-xs text-kumo-muted">
        出展者が申込フォームで入力した内容です。誤字修正や本人からの依頼による変更に使用してください。
      </p>

      <div className="flex flex-col gap-6">
        {/* 基本情報 */}
        <div>
          <h3 className="mb-3 text-sm font-semibold text-kumo-default">基本情報</h3>
          <div className="grid grid-cols-2 gap-3">
            <Field label="本名 (運営連絡用)">
              <Input
                data-testid="app-edit-realName"
                value={realName}
                onChange={(e) => setRealName(e.currentTarget.value)}
              />
            </Field>
            <Field label="出展者名 (公開・掲示用)">
              <Input
                data-testid="app-edit-exhibitorName"
                value={exhibitorName}
                onChange={(e) => setExhibitorName(e.currentTarget.value)}
              />
            </Field>
            <Field label="電話番号 (ハイフン無し)">
              <Input
                data-testid="app-edit-phone"
                value={phone}
                onChange={(e) => setPhone(e.currentTarget.value)}
              />
            </Field>
            <div data-testid="app-edit-type">
              <Select<string>
                label="出展種別"
                value={type}
                onValueChange={(v) => v && setType(v)}
                renderValue={(v) => TYPE_OPTIONS.find((o) => o.value === v)?.label ?? v}
              >
                {TYPE_OPTIONS.map((o) => (
                  <Select.Option key={o.value} value={o.value}>
                    {o.label}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <Field label="Web (任意)">
              <Input
                data-testid="app-edit-website"
                placeholder="https://example.com"
                value={website}
                onChange={(e) => setWebsite(e.currentTarget.value)}
              />
            </Field>
            <Field label="X / Twitter (任意)">
              <Input
                data-testid="app-edit-twitterId"
                value={twitterId}
                onChange={(e) => setTwitterId(e.currentTarget.value)}
              />
            </Field>
          </div>
        </div>

        <div className="h-px bg-kumo-line" />

        {/* 出展内容 */}
        <div>
          <h3 className="mb-3 text-sm font-semibold text-kumo-default">出展内容</h3>
          <div className="flex flex-col gap-3">
            <Field label="出展タイトル">
              <Input
                data-testid="app-edit-exhibitTitle"
                value={exhibitTitle}
                onChange={(e) => setExhibitTitle(e.currentTarget.value)}
              />
            </Field>
            <Field label="出展内容 (10文字以上)">
              <Textarea
                data-testid="app-edit-exhibitDescription"
                rows={4}
                value={exhibitDescription}
                onChange={(e) => setExhibitDescription(e.currentTarget.value)}
              />
            </Field>
            <Field label={`画像 (最大 ${MAX_IMAGES} 枚)`}>
              <div className="flex flex-col gap-2">
                {images.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                    {images.map((url, i) => (
                      <div key={url + i} className="relative">
                        <img src={url} alt={`画像 ${i + 1}`} className="h-20 w-full rounded-md object-cover" />
                        <button
                          type="button"
                          className="absolute right-1 top-1 rounded bg-black/60 p-1 text-white"
                          onClick={() => setImages((prev) => prev.filter((_, idx) => idx !== i))}
                        >
                          <Trash size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
                <Button
                  variant="secondary"
                  icon={UploadSimple}
                  loading={uploading}
                  disabled={images.length >= MAX_IMAGES}
                  onClick={() => fileInputRef.current?.click()}
                >
                  画像を追加
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    void handleImageUpload(e.currentTarget.files);
                    e.currentTarget.value = '';
                  }}
                />
              </div>
            </Field>
            <Checkbox.Group
              legend="出展日 (最低1日)"
              value={exhibitDays}
              onValueChange={setExhibitDays}
            >
              <Checkbox.Item value="saturday" label="2026年10月3日(土)" />
              <Checkbox.Item value="sunday" label="2026年10月4日(日)" />
            </Checkbox.Group>
          </div>
        </div>

        <div className="h-px bg-kumo-line" />

        {/* 机・椅子・電源・スポンサー */}
        <div>
          <h3 className="mb-1 text-sm font-semibold text-kumo-default">机・椅子・電源・少額スポンサー</h3>
          <p className="mb-3 text-xs text-kumo-muted">
            変更すると合計金額 (自動計算) が再計算されます。個別の料金override (final値) が設定済みの場合はそちらが優先されます。
          </p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div data-testid="app-edit-tableCount">
              <Select<string>
                label="机"
                value={String(tableCount)}
                onValueChange={(v) => v && setTableCount(Number(v))}
                renderValue={(v) => `${v} 台`}
              >
                {TABLE_COUNT_OPTIONS.map((n) => (
                  <Select.Option key={n} value={String(n)}>
                    {n} 台
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div data-testid="app-edit-chairCount">
              <Select<string>
                label="椅子"
                value={String(chairCount)}
                onValueChange={(v) => v && setChairCount(Number(v))}
                renderValue={(v) => `${v} 脚`}
              >
                {CHAIR_COUNT_OPTIONS.map((n) => (
                  <Select.Option key={n} value={String(n)}>
                    {n} 脚
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div data-testid="app-edit-powerWatts">
              <Select<string>
                label="電源"
                value={String(powerWatts)}
                onValueChange={(v) => v !== undefined && setPowerWatts(Number(v))}
                renderValue={(v) => (Number(v) > 0 ? `${v} W` : '不要')}
              >
                {POWER_WATTS_OPTIONS.map((n) => (
                  <Select.Option key={n} value={String(n)}>
                    {n > 0 ? `${n} W` : '不要'}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <Field label="スポンサー希望口数 (申込時の仮値・1口5,000円)">
              <Input
                data-testid="app-edit-sponsorUnits"
                type="number"
                min={0}
                value={String(sponsorUnits)}
                onChange={(e) => setSponsorUnits(Math.max(0, Number(e.currentTarget.value) || 0))}
              />
            </Field>
            <Field label="スポンサー表示名 (申込時の仮値・30字以内・URL不可)">
              <Input
                data-testid="app-edit-sponsorDisplayName"
                value={sponsorDisplayName}
                onChange={(e) => setSponsorDisplayName(e.currentTarget.value)}
                disabled={sponsorUnits === 0}
              />
            </Field>
          </div>
          {/* この 2 項目は「これから請求する分」を決める仮値であって、支援の実績ではない。
              実績 (bundled / addon / standalone) は sponsorships テーブル = /sponsorships 画面。 */}
          <p className="mt-2 text-xs text-kumo-muted">
            ここは請求額に上乗せする希望値です。入金済みの実績は「少額スポンサー」ページで確認してください。
          </p>
          {exhibitor.status === 'paid' && sponsorUnits !== (yearDetail?.sponsorUnits ?? 0) ? (
            <Banner variant="alert" className="mt-3">
              支払い済みの出展者です。ここで口数を変更しても、Stripe決済の確定記録 (sponsorships) には自動反映されません。
              金額の調整が必要な場合はStripeダッシュボードで個別に対応してください。
            </Banner>
          ) : null}
        </div>

        <div className="h-px bg-kumo-line" />

        {/* 搬入・特記事項 */}
        <div>
          <h3 className="mb-3 text-sm font-semibold text-kumo-default">搬入方法・特記事項</h3>
          <div className="flex flex-col gap-3">
            <Checkbox.Group
              legend="搬入方法 (複数選択可)"
              value={deliveryMethods}
              onValueChange={setDeliveryMethods}
            >
              <Checkbox.Item value="shipping" label="宅配便" />
              <Checkbox.Item value="car" label="車" />
            </Checkbox.Group>
            <Field label="特別リクエスト・配置希望 (任意)">
              <Textarea
                data-testid="app-edit-specialRequests"
                rows={3}
                value={specialRequests}
                onChange={(e) => setSpecialRequests(e.currentTarget.value)}
              />
            </Field>
          </div>
        </div>

        {feedback ? <Banner variant={feedback.type === 'ok' ? 'default' : 'error'}>{feedback.msg}</Banner> : null}

        <div>
          <Button
            data-testid="app-edit-save"
            variant="primary"
            icon={FloppyDisk}
            loading={busy}
            onClick={save}
          >
            申込内容を保存
          </Button>
        </div>
      </div>
    </Surface>
  );
}
